/**
 * Drag handles for the Vest position's TP / SL on a `.VEST` chart (#639).
 *
 * Mirrors the Tradovate LiveOrderLine UX: a pill on the line you grab and
 * drag; while dragging it shows the price, the distance from the entry and
 * the $ P&L at that level for the current size, snapped to the market's
 * tick; the change is sent on drop. A pill with an x removes the leg (no
 * confirm, like the Tradovate lines). With no TP or SL a "+TP" / "+SL" pill
 * sits on the entry line: it puts a draft line 1% away that is sent only
 * when dropped (its x discards it).
 *
 * The price lines themselves are drawn by useVestPositionLines, which reads
 * the same vestTpslStore, so the line follows the pill.
 *
 * Every other chart renders nothing: the gate is isVestFeedSymbol, and the
 * Tradovate order / position lines are a separate component this never
 * touches. Entries stay off: nothing here can place, flatten or resize.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isVestFeedSymbol, vestTickSize } from '../../utils/vestFeed';
import { vestRowMatchesChart, type VestPositionRow } from '../../utils/vestPositionLines';
import {
  defaultVestDraftPrice,
  snapVestPrice,
  vestLegKey,
  vestLegLabel,
  vestRowCommandable,
  vestRowDecimals,
  vestRowTick,
  vestSaneMark,
  type VestLegKind,
} from '../../utils/vestTpslDrag';
import { useVestPositionRows } from '../../store/vestPositionsStore';
import { useVestTpslStore } from '../../store/vestTpslStore';
import { addToast } from '../../store/toastStore';
import './VestTpslHandles.css';

interface SeriesLike {
  priceToCoordinate(price: number): number | null;
  coordinateToPrice(y: number): number | null;
}

export interface VestTpslHandlesProps {
  /** The price series (only priceToCoordinate / coordinateToPrice are used). */
  seriesRef: { readonly current: unknown };
  /** The element whose top is the price pane's y = 0 (the chart container). */
  containerRef: { readonly current: HTMLElement | null };
  symbol: string;
  lastPrice: number | null | undefined;
}

/** Pills sit left of the price lines' own titles (drawn by the chart next to the price scale). */
const PILL_RIGHT_PX = 210;

interface Pill {
  id: string;
  row: VestPositionRow;
  kind: VestLegKind | 'entry';
  price: number;
}

export const VestTpslHandles: React.FC<VestTpslHandlesProps> = ({ seriesRef, containerRef, symbol, lastPrice }) => {
  const isVest = isVestFeedSymbol(symbol);
  const rows = useVestPositionRows(isVest);
  const legs = useVestTpslStore(s => s.legs);
  const fallbackTick = isVest ? vestTickSize(symbol) : null;
  const chartRows = useMemo(
    () => (isVest ? rows.filter(r => vestRowMatchesChart(r, symbol) && vestRowCommandable(r)) : []),
    [isVest, rows, symbol],
  );

  // The leg being dragged by THIS chart: its start price, to tell a click from a drag.
  // `symbol` = the chart it started on: a drag never outlives a symbol change.
  const [drag, setDrag] = useState<{ row: VestPositionRow; kind: VestLegKind; startPrice: number; symbol: string } | null>(null);

  const pills: Pill[] = [];
  for (const row of chartRows) {
    pills.push({ id: `${row.brokerId}:${row.vestSymbol}:entry`, row, kind: 'entry', price: row.entryPrice });
    for (const kind of ['tp', 'sl'] as const) {
      const ov = legs[vestLegKey(row, kind)];
      const rowPrice = kind === 'tp' ? row.takeProfitPrice : row.stopLossPrice;
      const price = ov && ov.price !== null ? ov.price : rowPrice;
      if (price !== null) pills.push({ id: vestLegKey(row, kind), row, kind, price });
    }
  }

  // Keep every pill on its price as the chart scrolls / zooms (no React state per frame).
  const pillEls = useRef(new Map<string, HTMLDivElement>());
  const pillPrices = useRef(new Map<string, number>());
  pillPrices.current = new Map(pills.map(p => [p.id, p.price]));
  const hasPills = pills.length > 0;
  useEffect(() => {
    if (!hasPills) return;
    let raf = 0;
    const tick = () => {
      const series = seriesRef.current as SeriesLike | null;
      for (const [id, el] of pillEls.current) {
        const price = pillPrices.current.get(id);
        const y = series && price !== undefined ? series.priceToCoordinate(price) : null;
        if (y === null || y === undefined || !Number.isFinite(y)) {
          el.style.visibility = 'hidden';
        } else {
          el.style.visibility = '';
          el.style.top = `${y}px`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hasPills, seriesRef]);

  const priceAtClientY = useCallback((clientY: number, row: VestPositionRow): number | null => {
    const series = seriesRef.current as SeriesLike | null;
    const container = containerRef.current;
    if (!series || !container) return null;
    const y = clientY - container.getBoundingClientRect().top;
    const raw = series.coordinateToPrice(y);
    if (raw === null || !Number.isFinite(raw)) return null;
    return snapVestPrice(raw, vestRowTick(row, fallbackTick));
  }, [seriesRef, containerRef, fallbackTick]);

  // What the drag handlers read at event time (refs, so a price tick never
  // re-subscribes them - and never cancels a drag in progress).
  const live = useRef({ priceAtClientY, lastPrice, fallbackTick, symbol, chartRows });
  live.current = { priceAtClientY, lastPrice, fallbackTick, symbol, chartRows };

  // Symbol changed mid-drag: the drag ends (its cleanup puts the line back).
  useEffect(() => {
    if (drag && drag.symbol !== symbol) setDrag(null);
  }, [drag, symbol]);

  // Document-level move / release while a drag is on (same as LiveOrderLine).
  useEffect(() => {
    if (!drag) return;
    const store = useVestTpslStore.getState;
    const sameChart = () => live.current.symbol === drag.symbol && vestRowMatchesChart(drag.row, live.current.symbol);
    const move = (clientY: number) => {
      if (!sameChart()) return; // another chart's price scale: never read off it
      const p = live.current.priceAtClientY(clientY, drag.row);
      if (p !== null) store().dragTo(drag.row, drag.kind, p);
    };
    const end = () => {
      const cur = store().legs[vestLegKey(drag.row, drag.kind)];
      setDrag(null);
      if (!cur || cur.status !== 'dragging' || cur.price === null || cur.price === drag.startPrice) {
        store().cancelDrag(drag.row, drag.kind);
        return;
      }
      // The position must still be open, on this chart's market, on the same side.
      const l = live.current;
      const fresh = l.chartRows.find(r => r.brokerId === drag.row.brokerId && r.vestSymbol === drag.row.vestSymbol);
      if (!sameChart() || !fresh || fresh.side !== drag.row.side) {
        store().cancelDrag(drag.row, drag.kind);
        addToast('warning', `${drag.kind.toUpperCase()} not sent: the position changed while dragging`, 4000);
        return;
      }
      store().drop(fresh, drag.kind, cur.price, {
        mark: vestSaneMark(fresh, l.lastPrice),
        tick: vestRowTick(fresh, l.fallbackTick),
        decimals: vestRowDecimals(fresh, l.fallbackTick),
      });
    };
    const onMouseMove = (e: MouseEvent) => move(e.clientY);
    const onTouchMove = (e: TouchEvent) => { e.preventDefault(); if (e.touches[0]) move(e.touches[0].clientY); };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', end);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', end);
      // Interrupted (unmount, symbol change): never leave a fake price behind.
      // After a normal release this is a no-op (the leg is pending, a draft, or gone).
      store().cancelDrag(drag.row, drag.kind);
    };
  }, [drag]);

  if (!isVest || chartRows.length === 0) return null;

  const startDrag = (e: React.MouseEvent | React.TouchEvent, row: VestPositionRow, kind: VestLegKind, price: number) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    const ov = useVestTpslStore.getState().legs[vestLegKey(row, kind)];
    if (ov?.status === 'pending') return; // in flight: ignore further drags of this line
    e.stopPropagation();
    if (e.type !== 'touchstart') e.preventDefault();
    setDrag({ row, kind, startPrice: price, symbol });
  };

  return (
    <>
      {pills.map(p => {
        const decimals = vestRowDecimals(p.row, fallbackTick);
        const ref = (el: HTMLDivElement | null) => {
          if (el) pillEls.current.set(p.id, el);
          else pillEls.current.delete(p.id);
        };
        if (p.kind === 'entry') {
          const missing = (['tp', 'sl'] as const).filter(k => {
            const rowPrice = k === 'tp' ? p.row.takeProfitPrice : p.row.stopLossPrice;
            return rowPrice === null && !legs[vestLegKey(p.row, k)];
          });
          if (missing.length === 0) return null;
          return (
            <div
              key={p.id}
              ref={ref}
              className="vest-tpsl-add"
              data-testid="vest-tpsl-add"
              style={{ right: `${PILL_RIGHT_PX}px`, pointerEvents: 'auto' }}
            >
              {missing.map(k => (
                <button
                  key={k}
                  type="button"
                  className={`vest-tpsl-add-btn ${k}`}
                  data-testid={`vest-tpsl-add-${k}`}
                  title={`Add a ${k === 'tp' ? 'take-profit' : 'stop-loss'} line; drag it and release to send`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const tick = vestRowTick(p.row, fallbackTick);
                    const price = defaultVestDraftPrice(p.row, k, vestSaneMark(p.row, lastPrice), tick);
                    useVestTpslStore.getState().addDraft(p.row, k, price);
                  }}
                >
                  +{k.toUpperCase()}
                </button>
              ))}
            </div>
          );
        }
        const kind = p.kind;
        const ov = legs[p.id];
        const status = ov?.status ?? 'live';
        const label = vestLegLabel(p.row, p.price, decimals);
        const isDraft = status === 'draft' || ov?.draftPrice !== undefined;
        const pending = status === 'pending';
        return (
          <div
            key={p.id}
            ref={ref}
            className={`vest-tpsl-pill ${kind} ${status}${isDraft ? ' draft' : ''}`}
            data-testid={`vest-tpsl-pill-${kind}`}
            data-status={status}
            style={{ right: `${PILL_RIGHT_PX}px`, pointerEvents: 'auto', cursor: pending ? 'progress' : status === 'dragging' ? 'grabbing' : 'grab' }}
            onMouseDown={(e) => startDrag(e, p.row, kind, p.price)}
            onTouchStart={(e) => startDrag(e, p.row, kind, p.price)}
            title={pending ? 'Sending to Vest...' : isDraft ? 'Drag and release to send' : 'Drag to move; release sends it to Vest'}
          >
            <span className="vest-tpsl-grip" aria-hidden>::</span>
            <span className="vest-tpsl-kind">{kind.toUpperCase()}</span>
            <span className="vest-tpsl-price">{label.price}</span>
            <span className="vest-tpsl-dist">{label.distance}</span>
            <span className={`vest-tpsl-pnl ${label.pnlValue >= 0 ? 'profit' : 'loss'}`}>{label.pnl}</span>
            {pending && <span className="vest-tpsl-wait">...</span>}
            {!pending && status !== 'dragging' && (
              <button
                type="button"
                className="vest-tpsl-x"
                data-testid={`vest-tpsl-x-${kind}`}
                title={isDraft ? 'Discard this line' : `Remove the ${kind === 'tp' ? 'take-profit' : 'stop-loss'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDraft) useVestTpslStore.getState().discardDraft(p.row, kind);
                  else useVestTpslStore.getState().remove(p.row, kind);
                }}
              >
                x
              </button>
            )}
          </div>
        );
      })}
    </>
  );
};
